import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle } from 'lucide-react';

interface CarveoutSectionProps {
  providerInfo: any;
  form?: any;
  setForm?: React.Dispatch<React.SetStateAction<any>>;
  saving?: boolean;
  readonly?: boolean;
}

export const CarveoutSection = ({
  providerInfo,
  form,
  setForm,
  saving = false,
  readonly = false,
}: CarveoutSectionProps) => {
  const handleSwitchChange = (fieldName: string, checked: boolean) => {
    if (setForm) {
      setForm((prev: any) => ({ ...prev, [fieldName]: checked }));
    }
  };

  const carveouts = [
    { key: 'allows_medical_carveout', label: 'Medical Carve-out', description: 'Allow clients to carve out medical insurance' },
    { key: 'allows_ancillary_carveout', label: 'Ancillary Carve-out', description: 'Allow clients to carve out ancillary benefits' },
    { key: 'allows_workers_comp_carveout', label: 'Workers Comp Carve-out', description: 'Allow clients to carve out workers compensation' },
    { key: 'allows_epli_carveout', label: 'EPLI Carve-out', description: 'Allow clients to carve out Employment Practices Liability Insurance' },
    { key: 'allows_401k_carveout', label: '401k Carve-out', description: 'Allow clients to carve out 401k retirement plans' },
  ];

  if (readonly) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Carve-out Settings</h3>
        <p className="text-sm text-muted-foreground">
          Carve-outs allow clients to maintain their existing providers for specific benefits or services.
        </p>
        <div className="grid gap-4">
          {carveouts.map((carveout) => {
            const isAllowed = !!providerInfo[carveout.key];
            return (
              <div key={carveout.key} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  {isAllowed ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-300" />
                  )}
                  <div>
                    <Label className="text-sm font-medium">{carveout.label}</Label>
                    <p className="text-xs text-muted-foreground">{carveout.description}</p>
                  </div>
                </div>
                <Badge variant={isAllowed ? "default" : "secondary"}>
                  {isAllowed ? "Allowed" : "Not Allowed"}
                </Badge>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Carve-out Settings</h3>
      <p className="text-sm text-muted-foreground">
        Configure which services clients can carve out (maintain their existing providers for specific benefits).
      </p>
      <div className="grid gap-4">
        {carveouts.map((carveout) => (
          <div key={carveout.key} className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label className="text-sm font-medium">{carveout.label}</Label>
              <p className="text-xs text-muted-foreground">{carveout.description}</p>
            </div>
            <Switch 
              checked={!!form?.[carveout.key]}
              onCheckedChange={(checked) => handleSwitchChange(carveout.key, checked)}
              disabled={saving}
            />
          </div>
        ))}
      </div>
    </div>
  );
};